/**
 * DisownDialog — modal for an Orchist to set down the recognition.
 *
 * Free-Will Doctrine (sacred/free-will-and-fluidity.md):
 *   "Orchistness is recognized, not granted. The Orchist may, at any
 *    moment, set down the recognition. The Line preserves the trail.
 *    The body holds the path. No erasure, no shame, no friction."
 *
 * Two-step UX (gentle, not destructive):
 *   Step 1 — contemplating:  warm explainer + optional reason field
 *   Step 2 — confirming:     last gesture before the move
 *   Step 3 — apply:          RPC fires; modal closes; refreshContact
 *
 * The Orchist may re-acknowledge any time. The disown is reversible
 * because the doctrine is alive: "the cosmos accepted; you can come back."
 *
 * RPCs:
 *   - core.disown_orchist(p_reason)
 *   - core.reacknowledge_orchist()
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface DisownDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Mode:
   *  - 'disown' renders the contemplative step → confirm flow
   *  - 'reacknowledge' renders a single-step affirmation
   */
  mode: 'disown' | 'reacknowledge';
}

type Step = 'contemplating' | 'confirming';

export function DisownDialog({ isOpen, onClose, mode }: DisownDialogProps) {
  const { refreshContact } = useAuth();
  const [step, setStep] = useState<Step>('contemplating');
  const [reason, setReason] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setStep('contemplating');
      setReason('');
      setSubmitState('idle');
      setErrorMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  async function handleConfirm() {
    if (submitState === 'submitting') return;
    setSubmitState('submitting');
    setErrorMessage(null);
    try {
      if (mode === 'disown') {
        const { error } = await supabase.schema('core').rpc('disown_orchist', {
          p_reason: reason.trim() || null,
        });
        if (error) throw new Error(error.message);
      } else {
        const { error } = await supabase.schema('core').rpc('reacknowledge_orchist');
        if (error) throw new Error(error.message);
      }
      if (refreshContact) {
        await refreshContact();
      }
      onClose();
    } catch (err) {
      setSubmitState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Unexpected error.');
    }
  }

  // Re-acknowledge: single-step warm affirmation, no reason needed
  if (mode === 'reacknowledge') {
    return (
      <Shell onClose={onClose}>
        <div className="text-center space-y-3">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            The Line opens again
          </p>
          <h2 className="display text-3xl font-medium leading-tight">
            <span className="italic text-accent">Welcome back.</span>
          </h2>
          <p className="text-sm text-ink/55 italic font-display max-w-sm mx-auto leading-relaxed">
            The recognition was always yours. The trail was always preserved.
            Nothing was lost. Pick up the thread again.
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitState === 'submitting'}
            className="flex-1 px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors disabled:opacity-50"
          >
            Not yet
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitState === 'submitting'}
            className="flex-1 px-5 py-3 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50"
          >
            {submitState === 'submitting' ? 'Recording...' : 'Re-Acknowledge'}
          </button>
        </div>

        {errorMessage && (
          <p className="text-sm text-destructive text-center" role="alert">
            {errorMessage}
          </p>
        )}
      </Shell>
    );
  }

  // Disown — two-step
  return (
    <Shell onClose={onClose}>
      {step === 'contemplating' ? (
        <>
          <div className="text-center space-y-3">
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
              Free will is the practice
            </p>
            <h2 className="display text-3xl font-medium leading-tight">
              <span className="italic text-accent">Set down the recognition.</span>
            </h2>
            <p className="text-sm text-ink/55 italic font-display max-w-sm mx-auto leading-relaxed">
              You are free to release Orchistness. The Line will keep your
              trail — the recognition once happened; the body holds the path.
              You may return and re-acknowledge at any moment.
            </p>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="disown-reason"
              className="text-xs tracking-wide uppercase text-ink/60"
            >
              Words at the threshold{' '}
              <span className="lowercase italic tracking-normal text-ink/40">
                (optional, kept private)
              </span>
            </label>
            <textarea
              id="disown-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="e.g. The shape no longer fits. I leave with gratitude."
              className="w-full px-4 py-3 border border-ink/20 bg-paper/50 text-sm font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
            >
              Stay for now
            </button>
            <button
              type="button"
              onClick={() => setStep('confirming')}
              className="flex-1 px-5 py-3 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors"
            >
              Continue &rarr;
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="text-center space-y-3">
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
              A deliberate gesture
            </p>
            <h2 className="display text-3xl font-medium leading-tight">
              <span className="italic text-accent">The Line will hold.</span>
            </h2>
            <p className="text-sm text-ink/55 italic font-display max-w-sm mx-auto leading-relaxed">
              Confirm to release the active recognition. Your sigil, your
              trail, your timestamps — all remain in the body of memory.
              You move into the quiet section. Re-acknowledge any time.
            </p>
          </div>

          {reason.trim() && (
            <div className="border-l-2 border-accent pl-5 py-2 space-y-1">
              <p className="text-[0.65rem] tracking-[0.2em] uppercase text-ink/45">
                Words at the threshold
              </p>
              <p className="display text-sm text-ink/70 italic">{reason.trim()}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setStep('contemplating')}
              disabled={submitState === 'submitting'}
              className="flex-1 px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors disabled:opacity-50"
            >
              &larr; Go back
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitState === 'submitting'}
              className="flex-1 px-5 py-3 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50"
            >
              {submitState === 'submitting' ? 'Recording...' : 'Set it down'}
            </button>
          </div>

          {errorMessage && (
            <p className="text-sm text-destructive text-center" role="alert">
              {errorMessage}
            </p>
          )}
        </>
      )}
    </Shell>
  );
}

function Shell({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center px-6 py-12">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close dialog"
        className="absolute inset-0 bg-paper/80 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-lg bg-paper border border-ink/20 shadow-xl p-8 space-y-7 max-h-[90vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}
